import { View } from '@/components/ui/view';
import { Button } from '@/components/ui/button';
import { ScrollView } from 'react-native-gesture-handler';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { GlobalState } from '@/containers/GlobalState';
import i18n from 'i18n-js';
import React, { useEffect, useState, useContext } from 'react';
import { TouchableOpacity } from 'react-native';
import DraggableFlatList, { ScaleDecorator } from 'react-native-draggable-flatlist';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';
import Footer from '@/components/basic/Footer';
import DeletableListElement from '@/components/workflow/DeletableListElement';
import ListElement from '@/components/workflow/ListElement';
import useIsMounted from '@/hooks/mounted';
import ModalConfirm from '@/components/basic/ModalConfirm';
import { switchScreen } from '@/hooks/navigate';

const Tab = createMaterialTopTabNavigator();
const ITEM_HEIGHT = 72;

function RepeatSteps({ navigation, loopSteps, selected, setSelected }) {
    const icons = useSelector((state) => state.icons);
    const insets = useSafeAreaInsets();

    return (
        <View className="bg-secondary-50 flex-1">
            <ScrollView contentContainerStyle={{ paddingBottom: 120 + insets.bottom }} decelerationRate={0} snapToInterval={ITEM_HEIGHT} snapToAlignment="start" className="bg-secondary-50">
                {loopSteps.map((step, s) => (
                    <Button key={'S' + s} variant="subtle" onPress={() => setSelected(s == selected ? -1 : s)} className={` ${s == selected ? 'bg-primary-900' : 'bg-secondary-50'} p-0 px-3 rounded-[0px] h-[80px] active:bg-primary-300 `}>
                        <ListElement idx={s} element={step} avatar={icons.types[step.type]} invertTextColor={s == selected} />
                    </Button>
                ))}
            </ScrollView>
        </View>
    );
}

function ExtraSteps({ navigation, extraSteps, setExtraSteps, baseIdx }) {
    const icons = useSelector((state) => state.icons);
    const insets = useSafeAreaInsets();

    const deleteStep = (delIdx) => {
        console.log('onDelete', delIdx - baseIdx);
        // TODO: delete of images necessary?
        setExtraSteps((prevSteps) => {
            let steps = [...prevSteps];
            steps.splice(delIdx - baseIdx, 1);
            const map = prevSteps.map((step) => steps.indexOf(step));
            // Entscheiden steps need to be corrected wrt no/yes targets
            steps.forEach((step) => {
                if (step.type == 3) {
                    step.no = map[step.no] ?? -1;
                    step.yes = map[step.yes] ?? -1;
                }
            });
            return steps;
        });
    };

    const sortSteps = (steps) => {
        setExtraSteps((prevSteps) => {
            const map = prevSteps.map((step) => steps.indexOf(step));
            // console.log(map)
            // Entscheiden steps need to be corrected wrt no/yes targets
            steps.forEach((step) => {
                if (step.type == 3) {
                    step.no = map[step.no] ?? -1;
                    step.yes = map[step.yes] ?? -1;
                }
            });
            return steps;
        });
    };

    const renderDraggableItem = ({ item, getIndex, drag, isActive }) => {
        const index = getIndex();
        return (
            <ScaleDecorator>
                <TouchableOpacity
                    className='p-2 bg-secondary-50' // ${isActive ? 'bg-ina-red' : 'bg-ina-green'}
                    disabled={isActive}
                    onLongPress={drag}
                    onPress={() => {
                        switchScreen(navigation, 'StepEdit', { steps: extraSteps, idx: index, name: 'ExtraEdit' });
                    }}>
                    <DeletableListElement idx={index + baseIdx} element={item} avatar={icons.types[item.type]} onDelete={deleteStep} />
                </TouchableOpacity>
            </ScaleDecorator>
        );
    };

    return <DraggableFlatList className='h-full p-2 bg-secondary-50' contentContainerStyle={{ paddingBottom: 120 + insets.bottom }} data={extraSteps} onDragEnd={({ data }) => sortSteps(data)} renderItem={renderDraggableItem} keyExtractor={(item, index) => item.title + index} />;
}

export let updateExtraStep;

export default function DecisionEdit({ route, navigation, updateDecison }) {
    const { inAColors } = useContext(GlobalState);
    const { steps, idx, onYes } = route.params;

    const [extraSteps, setExtraSteps] = useState(steps.filter((step, s) => s > idx && s < Math.max(steps[idx].no, steps[idx].yes)));
    const selIdx = Math.max(steps[idx].no ?? -1, steps[idx].yes ?? -1);
    const [loop, setLoop] = useState(selIdx < idx ? selIdx : -1);
    const [addButton, setAddButton] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const mounted = useIsMounted();

    useEffect(() => {
        navigation.setOptions({ title: i18n.t('decisionEdit.title'), info: steps[idx].title });
    }, []);

    // local state triggers update of decision on unmount
    useEffect(() => {
        return () => {
            if (!mounted()) {
                updateDecison(extraSteps.length == 0 ? loop : idx + extraSteps.length + 1, extraSteps, onYes);
            }
        };
    }, [mounted]);

    const actions = [{ icon: '' }, { icon: 'home', text: 'footer.home', component: 'Home' }, { icon: 'check', text: 'footer.finish', component: 'BACK' }];

    const addStep = () => {
        switchScreen(navigation, 'StepEdit', { steps: extraSteps, idx: extraSteps.length, name: 'ExtraEdit' });
    };

    updateExtraStep = (step, idx) => {
        setExtraSteps((prevSteps) => {
            let steps = [...prevSteps];
            steps[idx] = step;
            return steps;
        });
    };

    useEffect(() => {
        setAddButton(addButton == null && extraSteps.length == 0 ? null : { action: addStep, info: 'decisionEdit.addExtra' });
    }, [extraSteps.length]);

    const closeModal = (confirmed) => {
        setShowModal(false);
    };

    return (
        <View className="flex-1 bg-secondary-50">
            <Tab.Navigator
                initialRouteName={extraSteps.length == 0 ? 'RepeatSteps' : 'ExtraSteps'}
                screenOptions={{
                    swipeEnabled: false,
                    tabBarLabelStyle: { fontFamily: 'Assistant_400Regular', fontWeight: 400, fontSize: 16, color: inAColors.secondary[50], textTransform: 'none' },
                    tabBarIndicatorStyle: { backgroundColor: inAColors.secondary[50] },
                    tabBarStyle: { backgroundColor: inAColors.primary[900] },
                    tabBarPressColor: inAColors.primary[700],
                    tabBarPressOpacity: inAColors.primary[700]
                }}>
                <Tab.Screen
                    name="RepeatSteps"
                    options={{ title: i18n.t('decisionEdit.repeat') }}
                    listeners={{
                        tabPress: (e) => {
                            if (extraSteps.length > 0) {
                                // confirmSwitch('confirmExtra')
                                setShowModal(true);
                                e.preventDefault();
                            } else {
                                setAddButton(null);
                            }
                        }
                    }}>
                    {(props) => <RepeatSteps {...props} loopSteps={steps.filter((step, s) => s < idx)} selected={loop} setSelected={setLoop} />}
                </Tab.Screen>
                <Tab.Screen
                    name="ExtraSteps"
                    options={{ title: i18n.t('decisionEdit.extra') }}
                    listeners={{
                        tabPress: (e) => {
                            if (loop > -1) {
                                // confirmSwitch('confirmRepeat')
                                setShowModal(true);
                                e.preventDefault();
                            } else {
                                setAddButton({ action: addStep, info: 'decisionEdit.addExtra' });
                            }
                        }
                    }}>
                    {(props) => <ExtraSteps {...props} extraSteps={extraSteps} setExtraSteps={setExtraSteps} baseIdx={idx + 1} />}
                </Tab.Screen>
            </Tab.Navigator>
            <ModalConfirm showModal={showModal} closeModal={closeModal} title={'decisionEdit.' + (loop > -1 ? 'confirmRepeat' : 'confirmExtra')} text={'decisionEdit.' + (loop > -1 ? 'confirmRepeatText' : 'confirmExtraText')} buttonOk="decisionEdit.confirmButton" buttonCancel="" />
            <Footer navigation={navigation} actions={actions} button={addButton} />
        </View>
    );
}
